/**
 * PageFocusContext — state-based focus channel (Phase 2C).
 *
 * Pages register their currently-focused entity (episode, guest, clip, sponsor)
 * by calling useRegisterPageFocus() in the component body. The useIvyPageContext
 * hook merges this with route-derived focus (Phase 2B) to produce the typed
 * PageContext sent to /api/ivy/chat on every turn.
 *
 * Merge precedence: route-derived focus wins on field-level conflict.
 * See Planning/phase-2c-pre-read.md §2 for rationale.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/** Focus fields populated by state-based pages.
 *
 *  `focusedPlannedEpisodeId` is intentionally NOT in this set — it lives in
 *  useIvyPageContext as route-only focus (Phase 2B) and takes precedence on merge.
 *
 *  `focusedAlertId` and `focusedContentId` are new in the schema-evolution phase,
 *  closing the gap for CastBackPage.selectedAlertId and YouTubeIntelPage.expandedContent.
 */
export interface PageFocus {
  focusedEpisodeId?: string
  focusedGuestId?: string
  focusedClipId?: string
  focusedSponsorId?: string
  focusedAlertId?: string
  focusedContentId?: string
}

interface PageFocusContextValue {
  focus: PageFocus
  setFocus: (patch: PageFocus) => void
}

const PageFocusContext = createContext<PageFocusContextValue | null>(null)

export function PageFocusProvider({ children }: { children: ReactNode }) {
  const [focus, setFocusState] = useState<PageFocus>({})

  // Replace semantics: each page publishes its COMPLETE desired focus set (or
  // an empty object to clear). Pages don't need to know about other pages' state.
  const setFocus = useCallback((patch: PageFocus) => {
    setFocusState(patch)
  }, [])

  const value = useMemo<PageFocusContextValue>(
    () => ({ focus, setFocus }),
    [focus, setFocus],
  )

  return <PageFocusContext.Provider value={value}>{children}</PageFocusContext.Provider>
}

export function usePageFocus(): PageFocusContextValue {
  const ctx = useContext(PageFocusContext)
  if (!ctx) {
    throw new Error('usePageFocus must be called within PageFocusProvider')
  }
  return ctx
}
